import { httpClient } from "@/shared/infrastructure/http/http-client";
import type { Alert } from "@/modules/alerts/domain/entities";
import type {
  Doctor,
  DoctorPatientLink,
  DoctorPatientStats,
  DoctorPatientSummary,
  DoctorRequest,
} from "../../domain/entities";
import type { DoctorsRepositoryPort, LinkPatientInput } from "../../application/ports/doctors.repository.port";

export const doctorsHttpRepository: DoctorsRepositoryPort = {
  linkPatient(input: LinkPatientInput): Promise<DoctorPatientLink> {
    const { doctorId, ...body } = input;
    return httpClient<DoctorPatientLink>(`/doctors/${doctorId}/patients`, { method: "POST", body });
  },

  async unlinkPatient(doctorId: string, patientId: string): Promise<void> {
    await httpClient<void>(`/doctors/${doctorId}/patients/${patientId}`, { method: "DELETE" });
  },

  listPatients(doctorId: string): Promise<DoctorPatientSummary[]> {
    return httpClient<DoctorPatientSummary[]>(`/doctors/${doctorId}/patients`);
  },

  listPatientRequests(doctorId: string): Promise<DoctorPatientLink[]> {
    return httpClient<DoctorPatientLink[]>(`/doctors/${doctorId}/patient-requests`);
  },

  listAlerts(doctorId: string): Promise<Alert[]> {
    return httpClient<Alert[]>(`/doctors/${doctorId}/alerts`);
  },

  getStats(doctorId: string): Promise<DoctorPatientStats> {
    return httpClient<DoctorPatientStats>(`/doctors/${doctorId}/stats`);
  },

  exportFhir(doctorId: string, patientId: string): Promise<any> {
    return httpClient<any>(`/doctors/${doctorId}/patients/${patientId}/export/fhir`);
  },

  getClinicalSummary(doctorId: string, patientId: string): Promise<any> {
    return httpClient<any>(`/doctors/${doctorId}/patients/${patientId}/clinical-summary`);
  },

  listPatientDoctors(userId: string): Promise<Doctor[]> {
    return httpClient<Doctor[]>(`/users/${userId}/doctors`);
  },

  listDoctorRequests(userId: string): Promise<DoctorRequest[]> {
    return httpClient<DoctorRequest[]>(`/users/${userId}/doctor-requests`);
  },

  acceptDoctorRequest(userId: string, linkId: string): Promise<DoctorPatientLink> {
    return httpClient<DoctorPatientLink>(`/users/${userId}/doctor-requests/${linkId}/accept`, { method: "POST" });
  },

  async declineDoctorRequest(userId: string, linkId: string): Promise<void> {
    await httpClient<void>(`/users/${userId}/doctor-requests/${linkId}/decline`, { method: "POST" });
  },

  async revokeDoctor(userId: string, doctorId: string): Promise<void> {
    await httpClient<void>(`/users/${userId}/doctors/${doctorId}`, { method: "DELETE" });
  },

  listDirectory(specialty?: string): Promise<Doctor[]> {
    const query = specialty ? `?specialty=${encodeURIComponent(specialty)}` : "";
    return httpClient<Doctor[]>(`/doctors${query}`);
  },

  getById(id: string): Promise<Doctor> {
    return httpClient<Doctor>(`/doctors/${id}`);
  },

  uploadAvatar(id: string, file: File): Promise<Doctor> {
    const formData = new FormData();
    formData.append("file", file);
    return httpClient<Doctor>(`/doctors/${id}/avatar`, { method: "POST", body: formData });
  },
};
